import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import style from "./PlatformDetailsStyle";
import PromptModal from "./PromptModal/PromptModal";
import {
  getEnterpriseById,
  updateEnterpriseDetails,
  getSubscriptionByEnterpriseId,
} from "../../../Services/enterpriseService";
import {
  getAdminsByEnterprise,
  createAdmin,
  deleteAdmin,
  resetAdminPassword,
} from "../../../Services/adminService";
import { 
  getAgentsByEnterprise, 
  createAgent, 
  updateAgentPrompt 
} from "../../../Services/agentService";
import toast from "react-hot-toast";

export default function PlatformDetails() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [enterprise, setEnterprise] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [admins, setAdmins] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const enterpriseId = parseInt(id, 10);
      const [enterpriseData, subData, adminData, agentData] = await Promise.all([
        getEnterpriseById(enterpriseId),
        getSubscriptionByEnterpriseId(enterpriseId),
        getAdminsByEnterprise(enterpriseId),
        getAgentsByEnterprise(enterpriseId),
      ]);

      if (enterpriseData) {
        setEnterprise(enterpriseData);
        setFormData(enterpriseData);
        setSubscription(subData);
        setAdmins(adminData);
        setAgents(agentData);
      } else {
        toast.error("Cliente não encontrado.");
        navigate("/platforms");
      }
    } catch (error) {
      console.error("Falha ao carregar dados do cliente:", error);
      toast.error("Falha ao carregar dados do cliente.");
      navigate("/platforms");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (
      ["street", "number", "neighborhood", "city", "state", "zipcode"].includes(name)
    ) {
      setFormData((prev) => ({
        ...prev,
        address: { ...prev.address, [name]: value },
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleCancel = () => {
    setFormData(enterprise);
    setIsEditing(false);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const updatedEnterprise = await updateEnterpriseDetails(enterprise.id, formData);
      if (updatedEnterprise) {
        toast.success("Dados salvos com sucesso!");
        setEnterprise(updatedEnterprise);
        setIsEditing(false);
      } else {
        toast.error("Falha ao salvar. Verifique os dados e tente novamente.");
      }
    } catch (error) {
      toast.error("Ocorreu um erro inesperado ao salvar.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddAdmin = async () => {
    const name = prompt("Nome do novo admin:");
    const loginId = prompt("Login ID do novo admin:");
    const password = prompt("Senha do novo admin:");
    if (!name || !loginId || !password) {
      toast.error("Todos os campos são obrigatórios.");
      return;
    }
    const adminData = { name, loginId, password, enterpriseId: enterprise.id };
    try {
      await createAdmin(adminData);
      toast.success("Admin adicionado com sucesso!");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || "Falha ao adicionar admin.");
    }
  };

  const handleDeleteAdmin = async (adminId) => {
    if (window.confirm("Tem certeza que deseja remover este admin?")) {
      try {
        await deleteAdmin(adminId);
        toast.success("Admin removido com sucesso!");
        fetchData();
      } catch (error) {
        toast.error("Falha ao remover admin.");
      }
    }
  };

  const handleResetPassword = async (adminId) => {
    const newPassword = prompt("Digite a nova senha:");
    if (!newPassword || newPassword.length < 6) {
      toast.error("A senha deve ter no mínimo 6 caracteres.");
      return;
    }
    try {
      await resetAdminPassword(adminId, newPassword);
      toast.success("Senha alterada com sucesso!");
    } catch (error) {
      toast.error(error.response?.data?.message || "Falha ao alterar a senha.");
    }
  };

  const handleAddAgent = async () => {
    const name = prompt("Nome do novo agente:");
    const realWhatsappNumber = prompt("Número de WhatsApp Real (com DDI, ex: 55119...):");
    const number = prompt("Número Virtual/ID do agente:");
    if (!name || !realWhatsappNumber || !number) {
      toast.error("Todos os campos são obrigatórios.");
      return;
    }
    const agentData = { name, realWhatsappNumber, number, enterpriseId: enterprise.id };
    try {
      await createAgent(agentData);
      toast.success("Agente criado com sucesso!");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || "Falha ao criar agente.");
    }
  };

  const handleSavePrompt = async (agent, newPrompt) => {
    try {
      await updateAgentPrompt(agent.number, newPrompt);
      toast.success("Prompt atualizado com sucesso!");
      setSelectedAgent(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || "Falha ao atualizar prompt.");
    }
  };

  const getStatusStyle = (status) => {
    if (status === 2) return style.statusActive;
    if (status === 3) return style.statusBlocked;
    return style.statusPending;
  };

  const getStatusText = (status) => {
    if (status === 2) return "Ativo";
    if (status === 3) return "Bloqueado";
    return "Pendente";
  };

  const maskToken = (token) => {
    if (!token || token.length < 20) return token || "Não definido";
    return `${token.substring(0, 15)}...${token.substring(token.length - 5)}`;
  };

  if (loading || !enterprise) {
    return <div style={{ color: "white", textAlign: "center", padding: "50px" }}>Carregando...</div>;
  }

  return (
    <>
      <div style={style.pageContainer}>
        <header style={style.pageHeader}>
          <div style={style.headerInfo}>
            <div>
              <h2 style={style.enterpriseName}>{enterprise.name}</h2>
              <p style={style.enterpriseCnpj}>CNPJ: {enterprise.cnpj}</p>
            </div>
          </div>
          <button style={style.backButton} onClick={() => navigate("/platforms")}>&larr; Voltar para a lista</button>
        </header>

        <div style={style.pageBody}>
          <div style={style.infoColumn}>
            <div style={style.infoBlock}>
              <h3 style={style.infoTitle}>Dados de Contato</h3>
              {isEditing ? (
                <>
                  <label style={style.inputLabel}>Email:</label>
                  <input name="email" value={formData.email || ""} onChange={handleChange} style={style.input} />
                  <label style={style.inputLabel}>Telefone:</label>
                  <input name="contact" value={formData.contact || ""} onChange={handleChange} style={style.input} />
                </>
              ) : (
                <>
                  <p style={style.infoText}><strong>Email:</strong> {enterprise.email}</p>
                  <p style={style.infoText}><strong>Telefone:</strong> {enterprise.contact}</p>
                </>
              )}
            </div>
            <div style={style.infoBlock}>
              <h3 style={style.infoTitle}>Endereço</h3>
              {isEditing ? (
                <>
                  <label style={style.inputLabel}>Rua:</label>
                  <input name="street" value={formData.address?.street || ""} onChange={handleChange} style={style.input}/>
                  <label style={style.inputLabel}>Número:</label>
                  <input name="number" value={formData.address?.number || ""} onChange={handleChange} style={style.input}/>
                  <label style={style.inputLabel}>Bairro:</label>
                  <input name="neighborhood" value={formData.address?.neighborhood || ""} onChange={handleChange} style={style.input}/>
                  <label style={style.inputLabel}>Cidade:</label>
                  <input name="city" value={formData.address?.city || ""} onChange={handleChange} style={style.input}/>
                  <label style={style.inputLabel}>Estado:</label>
                  <input name="state" value={formData.address?.state || ""} onChange={handleChange} style={style.input}/>
                  <label style={style.inputLabel}>CEP:</label>
                  <input name="zipcode" value={formData.address?.zipcode || ""} onChange={handleChange} style={style.input}/>
                </>
              ) : (
                <>
                  <p style={style.infoText}>{enterprise.address?.street}, {enterprise.address?.number}</p>
                  <p style={style.infoText}>{enterprise.address?.neighborhood}</p>
                  <p style={style.infoText}>{enterprise.address?.city} - {enterprise.address?.state}</p>
                  <p style={style.infoText}>CEP: {enterprise.address?.zipcode}</p>
                </>
              )}
            </div>
          </div>

          <div style={style.subscriptionColumn}>
            <div style={style.infoBlock}>
              <h3 style={style.infoTitle}>Assinatura</h3>
              {subscription ? (
                <div style={style.subscriptionCard}>
                  <div style={style.subscriptionHeader}>
                    <p style={style.subscriptionPlan}>{subscription.subscriptionType.name}</p>
                    <span style={{...style.statusBadge, ...getStatusStyle(subscription.subscription.status)}}>{getStatusText(subscription.subscription.status)}</span>
                  </div>
                  <p style={style.infoText}><strong>Valor:</strong> R$ {subscription.subscriptionType.value.toFixed(2)}</p>
                  <p style={style.infoText}>
                    <strong>Expira em:</strong> {new Date(subscription.subscription.expirationDate).toLocaleDateString("pt-BR")}
                  </p>
                </div>
              ) : (
                <p style={style.infoText}>Nenhuma assinatura encontrada para este cliente.</p>
              )}
            </div>
            <div style={style.infoBlock}>
              <h3 style={style.infoTitle}>Credenciais da Meta</h3>
              {isEditing ? (
                <>
                  <label style={style.inputLabel}>WhatsApp Token:</label>
                  <input name="whatsappToken" value={formData.whatsappToken || ""} onChange={handleChange} style={style.input} placeholder="EAA..." />
                  <label style={style.inputLabel}>Meta Account ID:</label>
                  <input name="metaAccountId" value={formData.metaAccountId || ""} onChange={handleChange} style={style.input} placeholder="ID da conta..."/>
                </>
              ) : (
                <>
                  <p style={style.infoText}><strong>WhatsApp Token:</strong> {maskToken(enterprise.whatsappToken)}</p>
                  <p style={style.infoText}><strong>Meta Account ID:</strong> {enterprise.metaAccountId || "Não definido"}</p>
                </>
              )}
            </div>
          </div>

          <div style={style.mainSection}>
            <div style={style.sectionHeader}>
                <h3 style={style.infoTitle}>Administradores ({admins.length} / {subscription?.subscription?.avaliableUsers ?? 'N/A'})</h3>
                <button 
                    style={(subscription?.subscription?.avaliableUsers ?? 0) > admins.length ? style.addButton : {...style.addButton, ...style['addButton:disabled']}} 
                    onClick={handleAddAdmin}
                    disabled={(subscription?.subscription?.avaliableUsers ?? 0) <= admins.length}
                >
                    + Adicionar Admin
                </button>
            </div>
            <div style={style.listContainer}>
                {admins.length > 0 ? admins.map(admin => (
                    <div key={admin.id} style={style.card}>
                        <div style={style.cardInfo}>
                            <p style={style.cardName}>{admin.name}</p>
                            <p style={style.cardSubtext}>Login: {admin.loginId}</p>
                        </div>
                        <div style={style.cardActions}>
                            <button onClick={() => handleResetPassword(admin.id)} style={style.actionButton}>Resetar Senha</button>
                            <button onClick={() => handleDeleteAdmin(admin.id)} style={{...style.actionButton, ...style.deleteButton}}>Remover</button>
                        </div>
                    </div>
                )) : <p style={style.noItemsText}>Nenhum administrador cadastrado.</p>}
            </div>
          </div>
          
          <div style={style.mainSection}>
            <div style={style.sectionHeader}>
                <h3 style={style.infoTitle}>Agentes</h3>
                <button style={style.addButton} onClick={handleAddAgent}>
                    + Adicionar Agente
                </button>
            </div>
            <div style={style.listContainer}>
                {agents.length > 0 ? agents.map(agent => (
                    <div key={agent.id} style={style.card}>
                        <div style={style.cardInfo}>
                            <p style={style.cardName}>{agent.name}</p>
                            <p style={style.cardSubtext}>WhatsApp: {agent.realWhatsappNumber}</p>
                        </div>
                        <div style={style.cardActions}>
                            <button onClick={() => setSelectedAgent(agent)} style={style.actionButton}>Prompt</button>
                        </div>
                    </div>
                )) : <p style={style.noItemsText}>Nenhum agente cadastrado.</p>}
            </div>
          </div>
        </div>

        <footer style={style.pageFooter}>
          {isEditing ? (
            <div style={style.buttonContainer}>
              <button onClick={handleCancel} style={{ ...style.actionButton, ...style.cancelButton }} disabled={isSaving}>Cancelar</button>
              <button onClick={handleSave} style={{ ...style.actionButton, ...style.saveButton }} disabled={isSaving}>
                {isSaving ? "Salvando..." : "Salvar"}
              </button>
            </div>
          ) : (
            <button onClick={() => setIsEditing(true)} style={{ ...style.actionButton, ...style.editButton }}>Editar Informações</button>
          )}
        </footer>
      </div>
      {selectedAgent && (
        <PromptModal
          agent={selectedAgent}
          onClose={() => setSelectedAgent(null)}
          onSave={handleSavePrompt}
        />
      )}
    </>
  );
}